import { anyspendService } from "../../../anyspend/services/anyspend.js";
import { normalizeAddress } from "../../../anyspend/utils/index.js";
import { useB3Config } from "../../../global-account/react/index.js";
import { useVisitorData } from "@fingerprintjs/fingerprintjs-pro-react";
import { useMutation } from "@tanstack/react-query";
import { useMemo } from "react";
/**
 * Hook for creating deposit_first orders in the Anyspend protocol.
 * This order type doesn't require srcAmount - the user deposits tokens after the order is created.
 */
export function useCreateDepositFirstOrder({
  onSuccess,
  onError
} = {}) {
  const {
    partnerId
  } = useB3Config();
  const {
    data: fpData
  } = useVisitorData({
    extendedResult: true
  }, {
    immediate: true
  });
  const visitorData = fpData && {
    requestId: fpData.requestId,
    visitorId: fpData.visitorId
  };
  const {
    mutate: createOrder,
    isPending
  } = useMutation({
    mutationFn: async params => {
      const {
        recipientAddress,
        srcChain,
        dstChain,
        srcToken,
        dstToken,
        creatorAddress,
        contractConfig
      } = params;
      // Build payload based on whether we have a contract config
      const payload = contractConfig ? {
        functionAbi: contractConfig.functionAbi,
        functionName: contractConfig.functionName,
        functionArgs: contractConfig.functionArgs,
        to: normalizeAddress(contractConfig.to),
        spenderAddress: contractConfig.spenderAddress ? normalizeAddress(contractConfig.spenderAddress) : undefined,
        action: contractConfig.action
      } : {};
      try {
        return await anyspendService.createOrder({
          recipientAddress: normalizeAddress(recipientAddress),
          type: "deposit_first",
          srcChain,
          dstChain,
          srcTokenAddress: normalizeAddress(srcToken.address),
          dstTokenAddress: normalizeAddress(dstToken.address),
          srcAmount: "",
          // Not required for deposit_first
          payload,
          metadata: {
            srcToken: {
              chainId: srcToken.chainId,
              address: srcToken.address,
              symbol: srcToken.symbol,
              name: srcToken.name,
              decimals: srcToken.decimals,
              metadata: srcToken.metadata || {}
            },
            dstToken: {
              chainId: dstToken.chainId,
              address: dstToken.address,
              symbol: dstToken.symbol,
              name: dstToken.name,
              decimals: dstToken.decimals,
              metadata: dstToken.metadata || {}
            }
          },
          creatorAddress: creatorAddress ? normalizeAddress(creatorAddress) : undefined,
          partnerId,
          visitorData,
          callbackMetadata: params.callbackMetadata
        });
      } catch (error) {
        if (error?.data) {
          throw error.data;
        }
        throw error;
      }
    },
    onSuccess: data => {
      onSuccess?.(data);
    },
    onError: error_0 => {
      onError?.(error_0);
    }
  });
  return useMemo(() => ({
    createOrder,
    isCreatingOrder: isPending
  }), [createOrder, isPending]);
}